import { Flex, Text } from '@chakra-ui/core'
import React, { useEffect } from 'react'
import useLocaleText from 'components/useLocaleText'
import { Controller, useFormContext } from 'react-hook-form'
import dayjs from 'dayjs'
import { getOrderedDays } from 'components/TasksV2/TaskCreation/Steps/TimeFrameSelection/FrequencyDatesPicker/utils'
import { DayCircle } from 'components/TasksV2/TaskCreation/Steps/TimeFrameSelection/FrequencyDatesPicker/DayCircle'
import { JobFrequencyType } from '../utils'
import { useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'

export const JobsDailySelector = ({ isActive = true }: { isActive?: boolean }) => {
  const isRtl = useSelector(isRtlSelector)
  const { watch, setValue, control, errors, clearError, triggerValidation, getValues } = useFormContext()
  const t_choose_days_of_week = useLocaleText('t_choose_days_of_week')
  const error_days_week = useLocaleText('error_days_week', true)

  // Retrieve `recurrence_frequency` from the form's state

  // Get `from_ts` from the form, defaulting to the current date if not present
  const fromTs = dayjs(dayjs())
  const startDayIndex = fromTs.day()

  // Get selected days of the week from the form's state
  const daysOfWeek: number[] = watch('job_data.recurrence_days_of_week') ?? []

  // Automatically set the default selected day to the `startDayIndex` if none are selected
  useEffect(() => {
    if (!daysOfWeek.length && isActive) {
      setValue('job_data.recurrence_days_of_week', [startDayIndex])
    }
    if (!isActive) {
      setValue('job_data.recurrence_days_of_week', [])
    }
    // eslint-disable-next-line
  }, [isActive])

  // Toggle the selection state of a day when clicked
  const toggleDay = async (dayIndex: number) => {
    const updatedDays = daysOfWeek.includes(dayIndex)
      ? daysOfWeek.filter((day) => day !== dayIndex) // Deselect the day
      : [...daysOfWeek, dayIndex] // Add the day to the selection

    // Update the form state with the new selection
    setValue('job_data.recurrence_days_of_week', updatedDays)

    // Trigger validation for the `days_of_week` field
    await triggerValidation('job_data.recurrence_days_of_week')

    // Clear errors if the field is now valid
    if (errors?.job_data?.recurrence_days_of_week && updatedDays.length > 0) {
      clearError('job_data.recurrence_days_of_week')
    }
  }

  const orderedDays = getOrderedDays(isRtl)

  return (
    <>
      {/* Display the title for the day selector */}
      <Text fontSize="12px">{t_choose_days_of_week}</Text>

      {/* Controlled field for `days_of_week` with validation rules */}
      <Controller
        name="job_data.recurrence_days_of_week"
        key="job_data.recurrence_days_of_week"
        control={control}
        defaultValue={[startDayIndex]} // Default to the current day index
        rules={{
          validate: (value) => {
            const freq = getValues('job_data.recurrence_frequency')

            if (Number(freq) !== JobFrequencyType.WEEKLY) return true
            return (Array.isArray(value) && value.length > 0) || error_days_week
          },
        }}
        as={
          <Flex justifyContent="space-between" my="30px">
            {/* Render a day circle for each day of the week */}
            {orderedDays.map(({ index, label }) => (
              <DayCircle
                key={index}
                day={label}
                isSelected={daysOfWeek.includes(index)} // Highlight if the day is selected
                onClick={() => toggleDay(index)} // Toggle selection on click
              />
            ))}
          </Flex>
        }
      />
    </>
  )
}
